// Angular
import { Injectable } from '@angular/core';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { QueryParamsModel } from '../../_base/crud';
import { AppState } from '../../../core/reducers';
import {
  ClearPractitionRoleErrors,
  PractitionRoleActionTypes,
  PractitionRoleCreated,
  PractitionRoleDeleted,
  PractitionRoleOnServerCreated,
  PractitionRoleOnServerDeleted,
  PractitionRoleOnServerUpdated,
  PractitionRolesActionToggleLoading,
  PractitionRolesPageCancelled,
  PractitionRolesPageLoaded,
  PractitionRolesPageRequested,
  PractitionRolesPageToggleLoading,
  PractitionRoleUpdated,
  ThrowPractitionRoleError,
} from '../_actions/practition-role.actions';
import { Router } from '@angular/router';
import { PractitionRoleService } from '../_services/practition-role.service';

@Injectable()
export class PractitionRoleEffects {
  showPageLoadingDistpatcher = new PractitionRolesPageToggleLoading({ isLoading: true });
  hidePageLoadingDistpatcher = new PractitionRolesPageToggleLoading({ isLoading: false });
  showActionLoadingDistpatcher = new PractitionRolesActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new PractitionRolesActionToggleLoading({ isLoading: false });

  @Effect()
  loadPractitionRolesPage$ = this.actions$.pipe(
    ofType<PractitionRolesPageRequested>(PractitionRoleActionTypes.PractitionRolesPageRequested),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showPageLoadingDistpatcher);
      const requestToServer = this.practitionRole.findPractitionRoles(payload.page);
      const lastQuery = of(payload.page);
      return forkJoin(requestToServer, lastQuery).pipe(
        catchError((error) => {
          return of([error]);
        }),
        map((response) => {
          if (response[0].error) {
            if (response[0].status === 401) {
              return response[0];
            } else if (response[0].status === 403) {
              this.router.navigateByUrl('/error/403');
              return new PractitionRolesPageCancelled();
            } else {
              this.store.dispatch(new ThrowPractitionRoleError({ error: response[0].error }));
              return new PractitionRolesPageCancelled();
            }
          } else {
            const result = response[0];
            const olives = [];
            // tslint:disable-next-line:no-shadowed-variable
            const lastQuery: QueryParamsModel = response[1];
            this.store.dispatch(new ClearPractitionRoleErrors());
            for (let i = 0; result.entry.length > i; i++) {
              olives.push(result.entry[i].resource);
            }
            return new PractitionRolesPageLoaded({
              practitionRoles: olives,
              totalCount: result.total,
              page: lastQuery,
            });
          }
        }),
      );
    }),
  );

  @Effect()
  deletePractitionRole$ = this.actions$.pipe(
    ofType<PractitionRoleOnServerDeleted>(PractitionRoleActionTypes.PractitionRoleOnServerDeleted),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.practitionRole.deletePractitionRole(payload.id).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map(() => {
          this.store.dispatch(this.hideActionLoadingDistpatcher);
          return new PractitionRoleDeleted(payload);
        }),
      );
    }),
  );

  @Effect()
  updatePractitionRole$ = this.actions$.pipe(
    ofType<PractitionRoleOnServerUpdated>(PractitionRoleActionTypes.PractitionRoleOnServerUpdated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.practitionRole.updatePractitionRole(payload.practitionRole).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res) {
            return new ThrowPractitionRoleError({ error: res });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(new ClearPractitionRoleErrors());
            return new PractitionRoleUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  createPractitionRole$ = this.actions$.pipe(
    ofType<PractitionRoleOnServerCreated>(PractitionRoleActionTypes.PractitionRoleOnServerCreated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.practitionRole.createPractitionRole(payload.practitionRole).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (res.status === 201) {
            this.store.dispatch(new ClearPractitionRoleErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new PractitionRoleCreated({ practitionRole: res });
          } else {
            return new ThrowPractitionRoleError({ error: res });
          }
        }),
      );
    }),
  );

  constructor(
    private actions$: Actions,
    private practitionRole: PractitionRoleService,
    private store: Store<AppState>,
    private router: Router,
  ) {}
}
